// pages/Community/CommunityInbox.jsx
//
// The "Inbox" tab of the Community messaging hub (Phase 1). Two-pane omnichannel
// inbox: contact threads (left) + the selected conversation with a reply box
// (right). Inbound arrives via channel webhooks; replies go out on the thread's
// channel. Scoped to the operator's company by the backend.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import addNotification from "helpers/notify";
import { getConversations, getConversation, replyConversation, markConversationRead, aiDraftConversation } from "api/messaging/messaging";
import {
  getConversations as getSkoolThreads, getConversation as getSkoolThread, sendConversationMessage as sendSkoolMessage,
  getSkoolProspects, setSkoolProspectState,
} from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

// Skool DM threads live in the CONNECTOR store (ConnectorConversation), not the
// messaging hub's — so they are fetched separately and merged here, keyed
// "skool:<id>" so the two id spaces can never collide. Opening and replying go
// through the same connector endpoints the Connectors → Skool DMs tab uses.
const SKOOL_PREFIX = "skool:";
const isSkool = (id) => String(id || "").startsWith(SKOOL_PREFIX);
const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const SOURCE_LABEL = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
const handleOf = (profileUrl) => String(String(profileUrl || "").split("/@")[1] || "").toLowerCase();

function skoolRow(c) {
  const t = (c.thread || []).filter((m) => String(m.text || "").trim());
  const last = t[t.length - 1] || c.lastMessage || {};
  return {
    _id: SKOOL_PREFIX + c._id, skoolId: c._id, channel: "skool",
    contactName: (c.contact && c.contact.name) || "(unknown)",
    handle: handleOf(c.contact && c.contact.profileUrl),
    lastDirection: last.direction || "", lastMessagePreview: String(last.text || c.lastMessagePreview || "").slice(0, 140),
    lastMessageAt: c.lastMessageAt || c.updatedAt, unread: false, skoolStatus: c.status,
  };
}

function ProspectPill({ p }) {
  if (!p) return null;
  const c = PROSPECT[p.state] || PROSPECT.new;
  return (
    <span data-testid="inbox-prospect-pill" title={`${c.label} (${SOURCE_LABEL[p.stateSource] || p.stateSource})${p.reason ? ` — ${p.reason}` : ""}`}
      style={{ fontSize: "0.66rem", fontWeight: 600, color: c.color, background: c.bg, borderRadius: 10, padding: "1px 7px", marginLeft: 6, whiteSpace: "nowrap" }}>
      {c.label}{p.stateSource === "manual" ? " ✎" : ""}
    </span>
  );
}

const CHANNEL_ICON = {
  whatsapp: { i: "fab fa-whatsapp", c: "#25D366" },
  telegram: { i: "fab fa-telegram", c: "#229ED9" },
  discord: { i: "fab fa-discord", c: "#5865F2" },
  slack: { i: "fab fa-slack", c: "#4A154B" },
  teams: { i: "fab fa-microsoft", c: "#6264A7" },
  skool: { i: "fas fa-graduation-cap", c: "#5b2ab5" },
};
const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch (_) { return ""; } };

const CommunityInbox = ({ brandId: brandIdProp }) => {
  const brandsRedux = useSelector((s) => s.user.brands) || [];
  // Internal AI-engine routing is shown only to super-admins (not tenants/admins).
  const isSuperAdmin = useSelector((s) => s.user.userInfo?.superAdmin === true);
  const [draftEngine, setDraftEngine] = useState(null);
  const [brandId, setBrandId] = useState(brandIdProp || "");
  const [rows, setRows] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const [prospects, setProspects] = useState({});   // Skool handle → ProspectState
  const [stateFilter, setStateFilter] = useState("");
  const [savingState, setSavingState] = useState(false);

  const loadProspects = useCallback(async () => {
    try {
      const res = await getSkoolProspects(brandId ? { brandId } : {});
      const map = {};
      ((res && res.prospects) || []).forEach((p) => { if (p.handle) map[String(p.handle).toLowerCase()] = p; });
      setProspects(map);
    } catch (_) { /* threads still work without states */ }
  }, [brandId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    loadProspects();
    // Both sources independently: one failing must not empty the other.
    const [hub, skool] = await Promise.allSettled([
      getConversations({ brandId: brandId || undefined }),
      getSkoolThreads({ platform: "skool", ...(brandId ? { brandId } : {}) }),
    ]);
    if (hub.status === "rejected") addNotification((hub.reason && hub.reason.message) || "Could not load inbox.", "danger");
    const a = hub.status === "fulfilled" ? ((hub.value && hub.value.data) || []) : [];
    const b = skool.status === "fulfilled" ? (((skool.value && (skool.value.data || skool.value.conversations)) || []).map(skoolRow)) : [];
    const at = (r) => new Date(r.lastMessageAt || r.updatedAt || 0).getTime();
    setRows([...a, ...b].sort((x, y) => at(y) - at(x)));
    setLoading(false);
  }, [brandId, loadProspects]);

  const prospectOf = (r) => (r && r.channel === "skool" && r.handle ? prospects[r.handle] || null : null);
  const shownRows = !stateFilter ? rows
    : stateFilter === "skool" ? rows.filter((r) => r.channel === "skool")
    : stateFilter === "unclassified" ? rows.filter((r) => r.channel === "skool" && !prospectOf(r))
    : rows.filter((r) => (prospectOf(r) || {}).state === stateFilter);

  const changeState = async (p, state) => {
    if (!p || !state) return;
    setSavingState(true);
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from the inbox");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      await loadProspects();
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not change the state", "danger"); }
    finally { setSavingState(false); }
  };

  useEffect(() => { loadList(); }, [loadList]);

  const openConv = useCallback(async (id) => {
    setActiveId(id);
    if (isSkool(id)) {
      try {
        const res = await getSkoolThread(id.slice(SKOOL_PREFIX.length));
        const c = (res && (res.data || res)) || {};
        setActive({
          _id: id, channel: "skool", contactName: (c.contact && c.contact.name) || "",
          handle: handleOf(c.contact && c.contact.profileUrl),
          messages: (c.thread || []).filter((m) => String(m.text || "").trim())
            .map((m) => ({ direction: m.direction, text: m.text, at: m.createdAt, status: m.status, error: m.error })),
        });
      } catch (e) { addNotification((e && e.message) || "Could not open the Skool thread.", "danger"); }
      return;
    }
    try {
      const res = await getConversation(id);
      setActive((res && res.data) || null);
      markConversationRead(id).then(() => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, unread: false } : r))));
    } catch (e) { addNotification((e && e.message) || "Could not open conversation.", "danger"); }
  }, []);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [active]);

  const [drafting, setDrafting] = useState(false);
  // "AI Draft": queue a brand-voice draft, then poll for it and drop it into the
  // reply box for the operator to review/edit/send.
  const aiDraft = useCallback(async () => {
    if (!activeId) return;
    setDrafting(true);
    setDraftEngine(null);
    try {
      await aiDraftConversation(activeId);
      // Poll generously — terminal/Claude-operator providers can take ~30-40s.
      let got = false;
      for (let i = 0; i < 22; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await getConversation(activeId);
        const d = res && res.data && res.data.aiDraft;
        if (d && d.text) {
          setText(d.text);
          // engineLabel only present for super-admins (backend-gated).
          if (d.engineLabel) setDraftEngine({ label: d.engineLabel, isFallback: !!d.isFallback });
          got = true; break;
        }
      }
      if (!got) addNotification("Still drafting — open the conversation again in a moment to grab it.", "info");
    } catch (e) { addNotification((e && e.message) || "AI draft failed.", "danger"); }
    finally { setDrafting(false); }
  }, [activeId]);

  const send = useCallback(async () => {
    const t = text.trim();
    if (!t || !activeId) return;
    setBusy(true);
    if (isSkool(activeId)) {
      // Skool replies go out through the connector lane (queued, paced, guarded).
      try {
        await sendSkoolMessage(activeId.slice(SKOOL_PREFIX.length), { text: t });
        setText(""); await openConv(activeId); loadList();
        addNotification("Reply queued on Skool", "success");
      } catch (e) { addNotification((e && e.message) || "Reply failed.", "danger"); }
      finally { setBusy(false); }
      return;
    }
    try {
      const res = await replyConversation(activeId, t);
      if (res && res.success) { setText(""); await openConv(activeId); loadList(); }
      else addNotification(`Reply failed: ${(res && res.error) || "unknown error"}`, "danger");
    } catch (e) { addNotification((e && e.message) || "Reply failed.", "danger"); }
    finally { setBusy(false); }
  }, [text, activeId, openConv, loadList]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 style={{ fontWeight: 700, margin: 0 }}>Inbox</h5>
        <div className="d-flex" style={{ gap: 8 }}>
          <select className="form-control form-control-sm" style={{ width: 200 }} value={brandId} onChange={(e) => { setBrandId(e.target.value); setActive(null); setActiveId(null); }}>
            <option value="">All brands</option>
            {brandsRedux.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
          <select className="form-control form-control-sm" style={{ width: 190 }} aria-label="Inbox prospect filter"
            value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="">All conversations</option>
            <option value="skool">Skool only</option>
            {PROSPECT_STATES.map((c) => <option key={c.key} value={c.key}>Skool: {c.label}</option>)}
            <option value="unclassified">Skool: not classified yet</option>
          </select>
          <button className="btn btn-sm btn-outline-secondary m-0" onClick={loadList}>Refresh</button>
        </div>
      </div>

      <div className="d-flex" style={{ gap: 12, height: 520, border: "1px solid #e3e6ec", borderRadius: 10, overflow: "hidden" }}>
        {/* Thread list */}
        <div style={{ width: 300, borderRight: "1px solid #e3e6ec", overflowY: "auto", background: "#fafbfc" }}>
          {loading ? <div className="p-3 text-muted">Loading…</div>
            : !shownRows.length ? <div className="p-3 text-muted" style={{ fontSize: "0.85rem" }}>{rows.length ? "Nothing matches this filter." : "No conversations yet. Inbound messages on connected channels appear here."}</div>
            : shownRows.map((r) => {
              const ic = CHANNEL_ICON[r.channel] || { i: "fas fa-comment", c: "#888" };
              return (
                <div key={r._id} onClick={() => openConv(r._id)}
                  style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #eef0f3", background: activeId === r._id ? "#eef4ff" : "transparent" }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <span style={{ fontWeight: r.unread ? 700 : 600, fontSize: "0.85rem" }}>
                      <i className={ic.i} style={{ color: ic.c, marginRight: 6 }} />{r.contactName || r.contactExternalId}
                      <ProspectPill p={prospectOf(r)} />
                    </span>
                    {r.unread ? <span style={{ width: 8, height: 8, borderRadius: 8, background: "#0b3d91" }} /> : null}
                  </div>
                  <div className="text-muted text-truncate" style={{ fontSize: "0.78rem" }}>
                    {r.lastDirection === "out" ? "You: " : ""}{r.lastMessagePreview}
                  </div>
                </div>
              );
            })}
        </div>

        {/* Conversation */}
        <div className="d-flex flex-column" style={{ flex: 1 }}>
          {!active ? <div className="d-flex align-items-center justify-content-center text-muted" style={{ flex: 1 }}>Select a conversation</div>
            : (
              <>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #e3e6ec", fontWeight: 600 }}>
                  {active.contactName || active.contactExternalId}
                  <span className="text-muted" style={{ fontWeight: 400, fontSize: "0.78rem", marginLeft: 8 }}>{active.channel}</span>
                  {active.channel === "skool" && (() => {
                    const p = active.handle ? prospects[active.handle] : null;
                    if (!p) return <div className="text-muted" style={{ fontWeight: 400, fontSize: "0.76rem" }}>Not classified yet — the classifier runs hourly.</div>;
                    const c = PROSPECT[p.state] || PROSPECT.new;
                    return (
                      <div data-testid="inbox-prospect-block" style={{ fontWeight: 400, fontSize: "0.76rem", marginTop: 6, padding: 8, borderRadius: 8, background: c.bg }}>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <b style={{ color: c.color }}>{c.label}</b>
                          <span className="text-muted">· {SOURCE_LABEL[p.stateSource] || p.stateSource}</span>
                          <select className="form-control form-control-sm ml-auto" style={{ width: 180 }} aria-label="Change prospect state"
                            value="" disabled={savingState} onChange={(e) => changeState(p, e.target.value)}>
                            <option value="">Change state…</option>
                            {PROSPECT_STATES.filter((x) => x.key !== p.state || p.stateSource !== "manual").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                            {p.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                          </select>
                        </div>
                        {p.reason && <div style={{ color: "#475467", marginTop: 3 }}>{p.reason}</div>}
                        {p.booking && p.booking.startsAt && <div style={{ color: "#6a3fb5", marginTop: 3 }}>Call booked: {fmt(p.booking.startsAt)}</div>}
                        <div className="text-muted" style={{ marginTop: 3 }}>{c.hint}</div>
                      </div>
                    );
                  })()}
                </div>
                <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, background: "#fff" }}>
                  {(active.messages || []).map((m, i) => (m.status === "failed" && m.direction === "out") ? (
                    <div key={i} data-testid="not-sent" className="d-flex" style={{ justifyContent: "flex-end", marginBottom: 8 }}>
                      <div title={m.text} style={{ maxWidth: "75%", padding: "4px 10px", borderRadius: 10, fontSize: "0.72rem", color: "#98a2b3", border: "1px dashed #d0d5dd" }}>
                        Not sent{m.error ? ` — ${m.error}` : ""} · {String(m.text || "").slice(0, 60)}…
                      </div>
                    </div>
                  ) : (
                    <div key={i} className="d-flex" style={{ justifyContent: m.direction === "out" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                      <div style={{ maxWidth: "75%", padding: "8px 12px", borderRadius: 12, fontSize: "0.85rem",
                        background: m.direction === "out" ? "#0b3d91" : "#f0f2f5", color: m.direction === "out" ? "#fff" : "#111" }}>
                        <div>{m.text}</div>
                        <div style={{ fontSize: "0.65rem", opacity: 0.7, marginTop: 3 }}>
                          {fmt(m.at)}{m.status === "failed" ? " • failed" : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: 10, borderTop: "1px solid #e3e6ec" }}>
                  {isSuperAdmin && draftEngine && (
                    <div style={{ fontSize: "0.72rem", marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 8px", borderRadius: 10,
                      background: draftEngine.isFallback ? "#fff4e5" : "#e7f5ee", color: draftEngine.isFallback ? "#9a6700" : "#1a7f37",
                      border: "1px solid " + (draftEngine.isFallback ? "#ffd8a8" : "#b7ebc6") }}>
                      <i className={draftEngine.isFallback ? "fas fa-life-ring" : "fas fa-bolt"} />
                      AI draft via {draftEngine.label}
                      <span style={{ opacity: 0.55 }}>· super-admin only</span>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    {active.channel !== "skool" && (
                      <button className="btn btn-sm btn-outline-primary m-0" disabled={busy || drafting} onClick={aiDraft} title="Generate an AI brand-voice draft reply">
                        {drafting ? "Drafting…" : "✨ AI Draft"}
                      </button>
                    )}
                    <input className="form-control form-control-sm" placeholder="Type a reply…" value={text}
                      onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} disabled={busy} />
                    <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }} disabled={busy || !text.trim()} onClick={send}>
                      {busy ? "…" : "Send"}
                    </button>
                  </div>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default CommunityInbox;
